// "When everyone has signed, also send the signed copy to…" — the
// completion-routing picker on Prepare & Send.
//
// CONSUMER: internal-ui. Mounted inside PrepareAndSend's right rail.
//
// Nate Swartz, 2026-08-26 (via Grant): while drafting an e-sign package
// he wants to name who receives the completed document — "a title
// company, a loan officer, or even a real estate agent on the other
// side of the transaction. I would like to find people by selecting a
// processing file, opportunity file, or typing a name/email." This
// component is that sentence, literally:
//
//   * the linked record's own people (parties, with their roles) are
//     offered before anyone types anything;
//   * typing searches contacts and vendors by name — and when what's
//     typed looks like an email address, offers it verbatim;
//   * "From another record…" searches Processing + Opportunity files
//     and lists the picked record's people to add from.
//
// Everything here composes EXISTING search machinery (parties rosters,
// the email-suggestions endpoint, vendor search, the unified record
// search) — no new read endpoints. What gets sent is a plain
// {email, name, source} list; the server re-validates every entry.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  api,
  type ContactEmailRecipientSuggestion,
  type RecordSearchResult,
  type Vendor,
} from "../../api";

// The type lives in a plain .ts file so modules outside React (the
// package-draft state, PR 8c) can name it.
import type { ForwardRecipient } from "./forward-recipient";
export type { ForwardRecipient };

/** One row the picker can offer, whatever machinery found it. */
interface Candidate {
  key: string;
  email: string | null;
  name: string;
  /** The grey line under the name — a role, a household, a vendor type. */
  detail: string;
  source: ForwardRecipient["source"];
  sourceDetail: string | null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 10;

export function ForwardRecipientsPicker({
  recordKind,
  recordId,
  recipients,
  onChange,
  hideHeading = false,
}: {
  /** The record this document is being sent FROM — its own people are
   *  the zero-typing offer. Kinds without a roster just start at the
   *  search box. */
  recordKind: string;
  recordId: string;
  recipients: ForwardRecipient[];
  onChange: (next: ForwardRecipient[]) => void;
  /** Set when the caller already prints "Afterwards" itself — the
   *  Prepare & Send panel's folding section does (2026-09-02), and two
   *  identical headings stacked on top of each other read as a bug. */
  hideHeading?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [people, setPeople] = useState<Candidate[]>([]);
  const [vendors, setVendors] = useState<Candidate[]>([]);
  // The linked record's roster, loaded once when the picker opens.
  const [ownParties, setOwnParties] = useState<Candidate[] | null>(null);
  // "From another record…" mode: search records, then browse the pick.
  const [recordMode, setRecordMode] = useState(false);
  const [recordHits, setRecordHits] = useState<RecordSearchResult[]>([]);
  const [browsing, setBrowsing] = useState<{ label: string; people: Candidate[] } | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const atCap = recipients.length >= MAX_RECIPIENTS;
  const has = useCallback(
    (email: string) => recipients.some((r) => r.email.toLowerCase() === email.toLowerCase()),
    [recipients],
  );

  const add = useCallback(
    (c: Candidate) => {
      if (!c.email || has(c.email) || atCap) return;
      onChange([
        ...recipients,
        {
          key: crypto.randomUUID(),
          email: c.email,
          name: c.name || null,
          source: c.source,
          sourceDetail: c.sourceDetail,
        },
      ]);
      setQuery("");
      inputRef.current?.focus();
    },
    [recipients, onChange, has, atCap],
  );

  // The linked record's own people — fetched once per open, so a
  // transaction's title company is one click with zero typing. An
  // opportunity's roster comes off its own parties endpoint (same
  // shape). A service_record send (the Contract packet card's "Prepare
  // & send" lands here) has no parties roster of its own, but the
  // Processing record delivering it does — resolve the SR's linked
  // transaction and offer THAT roster, so title/escrow still show up
  // (live audit, 2026-08-30: packet sends offered nobody). Failure
  // just means the list doesn't appear; search works.
  useEffect(() => {
    if (!open || ownParties !== null) return;
    if (
      recordKind !== "transaction" &&
      recordKind !== "opportunity" &&
      recordKind !== "service_record"
    ) {
      setOwnParties([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        // Only the fields the candidate rows read — the two parties
        // endpoints return sibling-but-distinct entry types.
        let res: {
          parties: Array<{
            key: string;
            email: string | null;
            name: string;
            role_label: string;
          }>;
        };
        if (recordKind === "transaction") {
          res = await api.listTransactionParties(recordId);
        } else if (recordKind === "opportunity") {
          res = await api.listOpportunityParties(recordId);
        } else {
          // service_record → its Processing record's roster.
          const detail = await api.getServiceRecordDetail(recordId);
          const transactionId = detail.processing_link?.transaction_id ?? null;
          if (!transactionId) {
            if (!cancelled) setOwnParties([]);
            return;
          }
          res = await api.listTransactionParties(transactionId);
        }
        if (cancelled) return;
        setOwnParties(
          res.parties.map((p) => ({
            key: `own:${p.key}`,
            email: p.email,
            name: p.name,
            detail: p.role_label,
            source: "party" as const,
            sourceDetail: p.role_label,
          })),
        );
      } catch {
        if (!cancelled) setOwnParties([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, ownParties, recordKind, recordId]);

  // Debounced search: contacts + vendors in name mode, records in
  // record mode. Both are read-only lookups; a failed call degrades to
  // an empty group, never an error state — the typed-email row and the
  // roster above stay usable regardless.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) {
      setPeople([]);
      setVendors([]);
      setRecordHits([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      void (async () => {
        try {
          if (recordMode) {
            const res = await api.searchRecords(q, {
              types: ["transaction", "opportunity"],
            });
            setRecordHits(res.results);
          } else {
            const [sugg, vend] = await Promise.all([
              api.getEmailSuggestions(q, 6).catch(() => ({ suggestions: [] })),
              api.searchVendors(q, 6).catch(() => ({ vendors: [] as Vendor[] })),
            ]);
            setPeople(
              sugg.suggestions.flatMap((s): Candidate[] => {
                if (s.kind !== "contact") return [];
                const c = s as ContactEmailRecipientSuggestion;
                return [
                  {
                    key: `contact:${c.contactId}`,
                    email: c.email,
                    name: c.preferredName || c.name,
                    detail: c.householdLabel,
                    source: "contact",
                    sourceDetail: null,
                  },
                ];
              }),
            );
            setVendors(
              vend.vendors
                .filter((v) => v.primary_contact_email)
                .map((v) => ({
                  key: `vendor:${v.id}`,
                  email: v.primary_contact_email,
                  name: v.display_name,
                  detail: v.primary_contact_name
                    ? `Vendor · ${v.primary_contact_name}`
                    : "Vendor",
                  source: "vendor" as const,
                  sourceDetail: v.display_name,
                })),
            );
          }
        } finally {
          setSearching(false);
        }
      })();
    }, 250);
    return () => clearTimeout(timer);
  }, [open, query, recordMode]);

  // Browse one picked record's people. A transaction offers its party
  // roster; an opportunity likewise. Each add is labeled with where it
  // came from, so "where did this contract go?" stays answerable.
  const browseRecord = useCallback(async (hit: RecordSearchResult) => {
    const label = hit.label || "that record";
    try {
      const res =
        hit.entity_type === "transaction"
          ? await api.listTransactionParties(hit.id)
          : await api.listOpportunityParties(hit.id);
      setBrowsing({
        label,
        people: res.parties.map((p) => ({
          key: `rec:${hit.id}:${p.key}`,
          email: p.email,
          name: p.name,
          detail: p.role_label,
          source: "record" as const,
          sourceDetail: `${hit.entity_type === "transaction" ? "Processing" : "Opportunity"}: ${label}`,
        })),
      });
      setQuery("");
    } catch {
      setBrowsing({ label, people: [] });
    }
  }, []);

  const typedEmail = EMAIL_RE.test(query.trim()) ? query.trim() : null;

  const candidateRow = (c: Candidate) => (
    <button
      key={c.key}
      type="button"
      className="esign-forward-candidate"
      disabled={!c.email || has(c.email ?? "") || atCap}
      onClick={() => add(c)}
      title={c.email ?? "No email on file"}
    >
      <span className="esign-forward-candidate-name">{c.name}</span>
      <span className="esign-forward-candidate-detail">
        {c.email ? c.detail : `${c.detail} — no email on file`}
      </span>
    </button>
  );

  return (
    <div className="esign-forward">
      {hideHeading ? null : <span className="esign-prep-eyebrow">Afterwards</span>}
      <p className="esign-forward-lead">
        When everyone has signed, also send the signed copy to…
      </p>

      {recipients.length > 0 ? (
        <div className="esign-forward-chips">
          {recipients.map((r) => (
            <span key={r.key} className="esign-forward-chip">
              <span className="esign-forward-chip-name">{r.name || r.email}</span>
              {r.sourceDetail ? (
                <span className="esign-forward-chip-detail">{r.sourceDetail}</span>
              ) : null}
              <button
                type="button"
                aria-label={`Remove ${r.name || r.email}`}
                onClick={() => onChange(recipients.filter((x) => x.key !== r.key))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {!open ? (
        <button
          type="button"
          className="esign-forward-add"
          onClick={() => setOpen(true)}
          disabled={atCap}
        >
          + Add a recipient
        </button>
      ) : (
        <div className="esign-forward-picker">
          {browsing ? (
            <>
              <button
                type="button"
                className="esign-forward-back"
                onClick={() => setBrowsing(null)}
              >
                ← Back to search
              </button>
              <p className="esign-forward-group-label">People on {browsing.label}</p>
              {browsing.people.length === 0 ? (
                <p className="esign-forward-empty">No people with roles on that record yet.</p>
              ) : (
                browsing.people.map(candidateRow)
              )}
            </>
          ) : (
            <>
              <input
                ref={inputRef}
                type="text"
                value={query}
                autoFocus
                placeholder={
                  recordMode
                    ? "Search Processing or Opportunity files…"
                    : "Name, company, or email address"
                }
                onChange={(e) => setQuery(e.target.value)}
              />

              {!recordMode && ownParties && ownParties.length > 0 && query.trim().length < 2 ? (
                <>
                  <p className="esign-forward-group-label">On this record</p>
                  {ownParties.map(candidateRow)}
                </>
              ) : null}

              {!recordMode && people.length > 0 ? (
                <>
                  <p className="esign-forward-group-label">People</p>
                  {people.map(candidateRow)}
                </>
              ) : null}
              {!recordMode && vendors.length > 0 ? (
                <>
                  <p className="esign-forward-group-label">Vendors</p>
                  {vendors.map(candidateRow)}
                </>
              ) : null}
              {!recordMode && typedEmail ? (
                candidateRow({
                  key: "typed",
                  email: typedEmail,
                  name: typedEmail,
                  detail: "Send to this address",
                  source: "address",
                  sourceDetail: null,
                })
              ) : null}
              {!recordMode &&
              query.trim().length >= 2 &&
              !searching &&
              people.length === 0 &&
              vendors.length === 0 &&
              !typedEmail ? (
                <p className="esign-forward-empty">
                  Nobody by that name — finish typing their email address to use it directly.
                </p>
              ) : null}

              {recordMode
                ? recordHits.map((hit) => (
                    <button
                      key={`${hit.entity_type}:${hit.id}`}
                      type="button"
                      className="esign-forward-candidate"
                      onClick={() => void browseRecord(hit)}
                    >
                      <span className="esign-forward-candidate-name">{hit.label}</span>
                      <span className="esign-forward-candidate-detail">
                        {hit.entity_type === "transaction" ? "Processing" : "Opportunity"}
                        {hit.sub_label ? ` · ${hit.sub_label}` : ""}
                      </span>
                    </button>
                  ))
                : null}

              <div className="esign-forward-foot">
                <button
                  type="button"
                  className="esign-forward-mode"
                  onClick={() => {
                    setRecordMode((v) => !v);
                    setQuery("");
                  }}
                >
                  {recordMode ? "← Search people instead" : "From another record…"}
                </button>
                <button
                  type="button"
                  className="esign-forward-done"
                  onClick={() => {
                    setOpen(false);
                    setRecordMode(false);
                    setBrowsing(null);
                    setQuery("");
                  }}
                >
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <p className="esign-prep-hint">
        They get the finished, signed document as a PDF attachment, emailed from your
        Outlook once everyone has signed — nothing goes to them before then.
        {atCap ? ` (${MAX_RECIPIENTS} is the limit.)` : ""}
      </p>
    </div>
  );
}
